import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Pagination } from '../pagination/pagination';
import { SearchInput } from './SearchInput';
import './SearchTable.css';
import { Translates } from '@/services/repository/i18n';

export interface PageQuery {
  pageSize: number;
  pageNumber: number;
  search: string;
}

export interface PageResponse<T> {
  pageSize: number;
  pageNumber: number;
  totalPages: number;
  data: T[] | undefined;
}

export interface SearchTableParams<T> {
  fetch: (q: PageQuery) => Promise<PageResponse<T>>;
  toolbox?: () => React.ReactElement;
  header?: () => React.ReactElement;
  row: (row: T, state: PageResponse<T>, setState: React.Dispatch<React.SetStateAction<PageResponse<T> | undefined>>) => React.ReactElement;
  pageQuery: PageQuery;
  translates: Translates
  className?: string;
}

export interface SearchTableRef {
  refresh: () => void;
}

export const SearchTable = forwardRef<SearchTableRef, SearchTableParams<any>>(
  <T,>(
    { row, fetch, pageQuery: initialPageQuery, translates, toolbox, header, className }: SearchTableParams<T>,
    ref: React.Ref<SearchTableRef>
  ) => {
    // Use local state for pageQuery
    const [currentQuery, setCurrentQuery] = useState<PageQuery>(initialPageQuery);
    const [resp, setResp] = useState<PageResponse<T>>();
    const [error, setError] = useState<string>();
    const respRef = React.useRef<PageResponse<T> | undefined>(resp);

    useEffect(() => {
      respRef.current = resp;
    }, [resp]);

    // Update response when currentQuery changes
    useEffect(() => {
      fetch(currentQuery).then(
        (response) =>
          setResp(
            response || {
              pageSize: currentQuery.pageSize,
              pageNumber: 1,
              totalPages: 0,
              data: [],
            }
          ),
        setError
      );
    }, [currentQuery, fetch]);

    // When props change, reset local state accordingly
    useEffect(() => {
      setCurrentQuery(initialPageQuery);
    }, [initialPageQuery]);

    // For imperative refresh (reload current query)
    useImperativeHandle(ref, () => ({
      refresh: () => setCurrentQuery({ ...currentQuery }),
    }));

    // Handlers
    const handleSearchDebounced = React.useCallback(
      (search: string) => {
        setCurrentQuery((q) => ({ ...q, pageNumber: 1, search }));
      },
      []
    );

    // To update immediately as you type (for SearchInput)
    const handleSearchChange = React.useCallback((v: string) => {
      setCurrentQuery((q) => ({ ...q, search: v }));
    }, []);

    // Pagination
    const handlePageChange = (pagination: { pageSize: number; pageNumber: number }) => {
      setCurrentQuery((q) => ({ ...q, ...pagination }));
    };

    if (error) return <div className="main-search-error">{error}</div>;

    if (!resp) return <div className="spinner"></div>;

    return (
      <div className={className || "main-search-table"}>
        <div className="toolbox">
          <div className="left">{toolbox && toolbox()}</div>
          <div className="right">
            <SearchInput
              value={currentQuery.search}
              onChange={handleSearchChange}
              onDebouncedChange={handleSearchDebounced}
              translates={translates}
            />
          </div>
        </div>
        <div className="search-body-wrap">
          <div className="search-body">
            {header && <div className="row header">{header()}</div>}
            {resp.data?.map((item) => (
              <div className="row" key={(item as any).id}>
                {row(item, resp, setResp)}
              </div>
            ))}
          </div>
        </div>
        {!resp.data?.length && <div className="empty-search-table-message">
          {translates.for("elements.search.empty_table")}
        </div>}
        <Pagination
          translates={translates}
          pageQuery={{
            pageSize: resp.pageSize,
            pageNumber: resp.pageNumber,
            totalPages: resp.totalPages,
            // No search in pagination—just relevant page fields
          }}
          onChange={handlePageChange}
        />
      </div>
    );
  }
);